import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useGameStore } from '../store/gameStore'
import { getAudioManager } from '../game/audio'
import {
  getChapter,
  getStage,
  listChapterStages,
  listChapters
} from '../game/campaign'
import {
  campaignProgress,
  campaignStarTotal,
  chapterStarTotal,
  clearsUnlockNext,
  formatStarsAria,
  isChapterCompleted,
  isChapterUnlocked,
  starSlots
} from '../progression/campaignProgress'
import {
  resolveContinueStageId,
  resolveContinueStageLabel
} from '../progression/continueStage'
import { useMetaStore } from '../meta/metaStore'
import { difficultyPreference } from '../progression/difficultyPreference'
import {
  DIFFICULTY_CATALOG,
  difficultyPowerPercent
} from '../game/combat/difficulty/catalog'
import type { DifficultyId } from '../game/combat/types/primitives'
import { DIFFICULTY_IDS } from '../game/combat/types/primitives'
import { emptyClears } from '../game/combat/campaign/stars'
import { listBuiltinMeta } from '../game/model/mapCatalog'
import { TOWER_CATALOG } from '../game/model/towers'

import {
  PRODUCT_COPY,
  productLevelLabel,
  productStageName
} from '../game/campaign/playerTerms'

type HomeView = 'hub' | 'chapters' | 'stages'

interface HomeMenuProps {
  onReplayTutorial?: () => void
}

function mapDisplayName(mapId: string): string {
  return listBuiltinMeta().find((m) => m.id === mapId)?.name ?? mapId
}

function chapterLockReason(chapterId: string): string {
  const chapters = listChapters()
  const idx = chapters.findIndex((c) => c.id === chapterId)
  if (idx <= 0) return ''
  const prev = chapters[idx - 1]!
  return `Complete ${productStageName(prev)} on Normal or higher.`
}

/**
 * Player HOME — hub, chapter select, and scalable stage browser (M61).
 */
export const HomeMenu: React.FC<HomeMenuProps> = ({ onReplayTutorial }) => {
  const startStage = useGameStore((state) => state.startStage)
  const chapters = listChapters()
  const [view, setView] = useState<HomeView>('hub')
  const [selectedChapterId, setSelectedChapterId] = useState(chapters[0]?.id ?? 'chapter-1')
  const [selectedStageId, setSelectedStageId] = useState('chapter1-stage1')
  const [difficultyId, setDifficultyId] = useState<DifficultyId>(() => difficultyPreference.load())
  const [progressTick, setProgressTick] = useState(0)
  const progress = campaignProgress.load()
  const ingots = useMetaStore((s) => s.ingots)
  const resetMeta = useMetaStore((s) => s.resetMeta)
  void progressTick

  const campaignStars = campaignStarTotal(progress)
  const continueId = resolveContinueStageId(progress)
  const continueLabel = resolveContinueStageLabel(progress)
  const continueStage = getStage(continueId)

  const chapterStages = useMemo(
    () => listChapterStages(selectedChapterId),
    [selectedChapterId]
  )
  const selectedChapter = getChapter(selectedChapterId)
  const chapterUnlocked = isChapterUnlocked(selectedChapterId, progress)
  const chapterStars = chapterStarTotal(progress, selectedChapterId)
  const chapterDone = isChapterCompleted(selectedChapterId, progress)

  const selected = getStage(selectedStageId) ?? chapterStages[0]
  const selectedUnlocked = selected ? campaignProgress.isUnlocked(selected.id) : false
  const difficulty = DIFFICULTY_CATALOG[difficultyId]
  const selectedEntry = selected ? progress.stages[selected.id] : undefined
  const selectedClears = selectedEntry?.clears ?? emptyClears()
  const firstClearReward = selected ? selected.firstClearRewards[difficultyId] : 0

  const handleResetProgress = () => {
    if (
      !window.confirm(
        'Reset campaign progress, stars, Ingots, and all tower Research? Tutorial, audio, and editor drafts are kept.'
      )
    ) {
      return
    }
    resetMeta()
    setView('hub')
    setSelectedChapterId(chapters[0]?.id ?? 'chapter-1')
    setSelectedStageId('chapter1-stage1')
    setProgressTick((n) => n + 1)
  }

  const selectDifficulty = (id: DifficultyId) => {
    setDifficultyId(id)
    difficultyPreference.save(id)
  }

  const openChapter = (chapterId: string) => {
    if (!isChapterUnlocked(chapterId, progress)) return
    const stages = listChapterStages(chapterId)
    setSelectedChapterId(chapterId)
    const firstOpen =
      stages.find((s) => {
        if (!campaignProgress.isUnlocked(s.id)) return false
        const clears = progress.stages[s.id]?.clears
        return !clears || !clearsUnlockNext(clears)
      }) ?? stages[0]
    if (firstOpen) setSelectedStageId(firstOpen.id)
    setView('stages')
  }

  const playSelected = () => {
    if (!selected || !selectedUnlocked) return
    getAudioManager().unlock()
    getAudioManager().play('ui.confirm')
    difficultyPreference.save(difficultyId)
    startStage(selected.id, difficultyId)
  }

  /** CONTINUE opens stage detail / pre-play — never launches the match. */
  const openContinue = () => {
    if (!continueStage) return
    getAudioManager().unlock()
    getAudioManager().play('ui.confirm')
    const chapter = getChapter(continueStage.chapterId)
    if (chapter) setSelectedChapterId(chapter.id)
    setSelectedStageId(continueStage.id)
    setView('stages')
  }

  if (view === 'hub') {
    return (
      <section className="home-menu home-menu--hub" aria-label="Main menu">
        <div className="home-menu__hero">
          <h1 className="home-menu__title">SHAPES TD</h1>
          <p className="home-menu__subtitle">Geometric Tower Defense</p>
        </div>

        <div className="home-menu__meta-row" data-testid="campaign-meta">
          <span className="home-menu__ingots" data-testid="ingots-balance" title="Persistent Ingots">
            Ingots {ingots}
          </span>
          <span
            className="home-menu__stars-total"
            data-testid="campaign-stars"
            aria-label={`${campaignStars.earned} of ${campaignStars.max} stars`}
          >
            {campaignStars.earned} / {campaignStars.max} ★
          </span>
        </div>

        <div className="home-menu__hub-actions">
          <button
            type="button"
            className="home-menu__start home-menu__continue home-menu__continue--primary"
            data-testid="home-continue"
            disabled={!continueStage}
            onPointerUp={(event) => {
              event.preventDefault()
              openContinue()
            }}
          >
            <span className="home-menu__continue-label">CONTINUE</span>
            <span className="home-menu__continue-meta">
              {continueLabel}
              {continueStage ? ` · ${difficulty.displayName.toUpperCase()}` : ''}
            </span>
          </button>

          <button
            type="button"
            className="home-menu__hub-btn home-menu__hub-btn--secondary"
            data-testid="home-campaign"
            onPointerUp={(event) => {
              event.preventDefault()
              setView('chapters')
            }}
          >
            CAMPAIGN
          </button>

          <Link
            to="/research"
            className="home-menu__hub-btn home-menu__hub-btn--link home-menu__hub-btn--secondary"
            data-testid="home-research-link"
          >
            RESEARCH
          </Link>
        </div>

        <button
          type="button"
          className="home-menu__replay-tutorial"
          data-testid="replay-tutorial"
          onPointerUp={(event) => {
            event.preventDefault()
            onReplayTutorial?.()
          }}
        >
          REPLAY TUTORIAL
        </button>

        <button
          type="button"
          className="home-menu__reset-progress"
          data-testid="reset-progress"
          onPointerUp={(event) => {
            event.preventDefault()
            handleResetProgress()
          }}
        >
          Reset campaign + Research
        </button>

        <p className="home-menu__admin-link">
          <Link to="/map-editor" data-testid="map-editor-link">
            Map editor (dev)
          </Link>
        </p>
      </section>
    )
  }

  if (view === 'chapters') {
    return (
      <section className="home-menu home-menu--chapters" aria-label={PRODUCT_COPY.stageSelectAria}>
        <div className="home-menu__nav-row">
          <button
            type="button"
            className="home-menu__back"
            data-testid="chapters-back"
            onPointerUp={(event) => {
              event.preventDefault()
              setView('hub')
            }}
          >
            {PRODUCT_COPY.backToHome}
          </button>
          <span className="home-menu__ingots" data-testid="ingots-balance">
            Ingots {ingots}
          </span>
        </div>

        <h1 className="home-menu__section-heading">{PRODUCT_COPY.campaignHeading}</h1>
        <p className="home-menu__chapter-hint">
          {campaignStars.earned} / {campaignStars.max} ★ across all stages
        </p>

        <ul className="home-menu__chapter-list" data-testid="chapter-list">
          {chapters.map((chapter) => {
            const unlocked = isChapterUnlocked(chapter.id, progress)
            const stars = chapterStarTotal(progress, chapter.id)
            const done = isChapterCompleted(chapter.id, progress)
            const stageName = productStageName(chapter)
            const statusClass = !unlocked
              ? ' home-menu__chapter-card--locked'
              : done
                ? ' home-menu__chapter-card--complete'
                : ' home-menu__chapter-card--progress'
            const statusText = !unlocked
              ? chapterLockReason(chapter.id)
              : done
                ? 'COMPLETE'
                : 'IN PROGRESS'
            return (
              <li key={chapter.id}>
                <button
                  type="button"
                  className={`home-menu__chapter-card${statusClass}`}
                  data-testid={`chapter-card-${chapter.id}`}
                  aria-disabled={!unlocked}
                  aria-label={`${stageName}, ${stars.earned} of ${stars.max} stars, ${statusText}`}
                  disabled={!unlocked}
                  onPointerUp={(event) => {
                    event.preventDefault()
                    openChapter(chapter.id)
                  }}
                >
                  <span className="home-menu__chapter-card-top">
                    <span className="home-menu__chapter-card-title">{stageName}</span>
                    <span
                      className={`home-menu__chapter-badge${
                        !unlocked
                          ? ' home-menu__chapter-badge--locked'
                          : done
                            ? ' home-menu__chapter-badge--complete'
                            : ' home-menu__chapter-badge--progress'
                      }`}
                      aria-hidden
                    >
                      {!unlocked ? 'LOCKED' : done ? 'DONE' : 'OPEN'}
                    </span>
                  </span>
                  <span className="home-menu__chapter-card-stars">
                    {stars.earned} / {stars.max} ★
                  </span>
                  <span className="home-menu__chapter-card-status">{statusText}</span>
                </button>
              </li>
            )
          })}
        </ul>
      </section>
    )
  }

  // stages view
  return (
    <section className="home-menu home-menu--stages" aria-label={PRODUCT_COPY.levelSelectAria}>
      <div className="home-menu__nav-row">
        <button
          type="button"
          className="home-menu__back"
          data-testid="stages-back"
          onPointerUp={(event) => {
            event.preventDefault()
            setView('chapters')
          }}
        >
          {PRODUCT_COPY.backToStages}
        </button>
        <span className="home-menu__ingots" data-testid="ingots-balance">
          Ingots {ingots}
        </span>
      </div>

      <header className="home-menu__stage-header">
        <h1 className="home-menu__section-heading" data-testid="chapter-title">
          {productStageName(selectedChapter)}
        </h1>
        <p className="home-menu__chapter-hint" data-testid="chapter-stars">
          {chapterStars.earned} / {chapterStars.max} ★
          {chapterDone ? ' · Complete' : ''}
          {!chapterUnlocked ? ` · ${chapterLockReason(selectedChapterId)}` : ''}
        </p>
      </header>

      <ul className="home-menu__stage-grid" data-testid="stage-list">
        {chapterStages.map((stage) => {
          const unlocked = campaignProgress.isUnlocked(stage.id)
          const active = stage.id === selectedStageId
          const clears = progress.stages[stage.id]?.clears ?? emptyClears()
          const slots = starSlots(clears)
          const filled = slots.filter((s) => s.filled).length
          const mastered = filled >= 4
          const cleared = filled > 0
          const stateClass = !unlocked
            ? ' home-menu__stage-tile--locked'
            : mastered
              ? ' home-menu__stage-tile--mastery'
              : cleared
                ? ' home-menu__stage-tile--cleared'
                : ' home-menu__stage-tile--available'
          return (
            <li key={stage.id}>
              <button
                type="button"
                className={`home-menu__stage-tile${active ? ' home-menu__stage-tile--active' : ''}${stateClass}`}
                data-testid={`stage-card-${stage.displayLabel}`}
                aria-pressed={active}
                aria-current={active ? 'true' : undefined}
                aria-disabled={!unlocked}
                disabled={!unlocked}
                aria-label={`${stage.displayLabel}, ${formatStarsAria(clears)}${
                  unlocked ? (mastered ? ', mastered' : cleared ? ', cleared' : ', available') : ', locked'
                }`}
                onPointerUp={(event) => {
                  event.preventDefault()
                  if (!unlocked) return
                  setSelectedStageId(stage.id)
                }}
              >
                <span className="home-menu__stage-tile-label">{stage.displayLabel}</span>
                <span className="home-menu__stage-tile-stars" aria-hidden>
                  {filled > 0 ? `${filled}★` : unlocked ? '—' : '🔒'}
                </span>
                {active ? (
                  <span className="home-menu__stage-tile-selected" aria-hidden>
                    ●
                  </span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ul>

      {selected && (
        <div className="home-menu__stage-detail" data-testid="stage-detail">
          <h2 className="home-menu__detail-title">
            {PRODUCT_COPY.levelPrefix} {productLevelLabel(selected)}
            {selectedUnlocked ? '' : ' · Locked'}
            {selected.bossLevel ? ' · BOSS' : ''}
          </h2>
          <p className="home-menu__detail-line">
            Map {mapDisplayName(selected.mapId)} · {selected.waves.length} waves
            {' · '}
            Towers{' '}
            {selected.allowedTowerIds ? (
              <span className="home-menu__tower-glyphs" aria-label={selected.allowedTowerIds.map((id) => TOWER_CATALOG[id]?.name ?? id).join(', ')}>
                {selected.allowedTowerIds.map((id) => {
                  const tower = TOWER_CATALOG[id]
                  if (!tower) return null
                  const color = `#${tower.color.toString(16).padStart(6, '0')}`
                  return (
                    <span
                      key={id}
                      className="home-menu__tower-glyph"
                      style={{ ['--tower-color' as string]: color }}
                      title={tower.name}
                    >
                      <span className={`tower-glyph tower-glyph--${id}`} aria-hidden />
                    </span>
                  )
                })}
              </span>
            ) : (
              'All'
            )}
          </p>

          <div
            className="home-menu__mastery"
            aria-label={formatStarsAria(selectedClears)}
            data-testid={`stage-stars-${selected.displayLabel}`}
          >
            <span className="home-menu__mastery-label">Mastery</span>
            <div className="home-menu__mastery-slots">
              {starSlots(selectedClears).map((slot) => {
                const short =
                  slot.difficulty === 'easy'
                    ? 'E'
                    : slot.difficulty === 'normal'
                      ? 'N'
                      : slot.difficulty === 'hard'
                        ? 'H'
                        : 'I'
                const name = DIFFICULTY_CATALOG[slot.difficulty].displayName
                return (
                  <span
                    key={slot.difficulty}
                    className={`home-menu__mastery-slot${
                      slot.filled ? ' home-menu__mastery-slot--filled' : ''
                    }${slot.difficulty === difficultyId ? ' home-menu__mastery-slot--focus' : ''}`}
                    title={`${name}${slot.filled ? ' cleared' : ' not cleared'}`}
                  >
                    <span className="home-menu__mastery-diff" aria-hidden>
                      {short}
                    </span>
                    <span className="home-menu__mastery-star" aria-hidden>
                      {slot.filled ? '★' : '☆'}
                    </span>
                  </span>
                )
              })}
            </div>
          </div>

          <div className="home-menu__difficulty" data-testid="difficulty-picker">
            <h3 className="home-menu__section-label">Difficulty</h3>
            <div
              className="home-menu__difficulty-seg"
              role="radiogroup"
              aria-label="Difficulty"
            >
              {DIFFICULTY_IDS.map((id) => {
                const def = DIFFICULTY_CATALOG[id]
                const active = id === difficultyId
                const cleared = selectedClears[id]
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`home-menu__difficulty-seg-btn${
                      active ? ' home-menu__difficulty-seg-btn--active' : ''
                    }`}
                    data-testid={`difficulty-${id}`}
                    onPointerUp={(event) => {
                      event.preventDefault()
                      selectDifficulty(id)
                    }}
                  >
                    {def.displayName}
                    {cleared ? ' ✓' : ''}
                  </button>
                )
              })}
            </div>
            <p className="home-menu__difficulty-info" data-testid="difficulty-info">
              Start {difficulty.startingCoins} Coins · Enemy power{' '}
              {difficultyPowerPercent(difficultyId)}%
              {firstClearReward > 0
                ? ` · First clear +${firstClearReward} ◆${
                    selectedClears[difficultyId] ? ' claimed' : ''
                  }`
                : ''}
            </p>
          </div>

          <p className="home-menu__play-intent" data-testid="play-intent" aria-live="polite">
            {selected.displayLabel} · {difficulty.displayName.toUpperCase()}
          </p>

          <button
            type="button"
            className="home-menu__start"
            disabled={!selectedUnlocked}
            data-testid="home-play"
            onPointerUp={(event) => {
              event.preventDefault()
              playSelected()
            }}
          >
            PLAY
          </button>
        </div>
      )}
    </section>
  )
}
